import { Pagination } from '@/domain/models'
import type { Plan } from '@/domain/models/plan-license/plan-model'

// ─── Estados / Cidades / Unidades ────────────────────────────────────────────
//
// Tipos usados pelas etapas de seleção de unidade presencial no wizard.
// O fluxo é encadeado: estados → cidades (por estado) → unidades (por cidade).

// Parâmetros base compartilhados pelas queries de estados, cidades e unidades
export interface ProductValuesParams {
  productId: number
  // Quando true, aplica filtros de elegibilidade para dependentes (ex: exclui unidades restritas)
  isDependentContext?: boolean
}

// Parâmetros para buscar as cidades disponíveis de um estado específico
export interface ProductCityValuesParams extends ProductValuesParams {
  state: string
}

// Parâmetros para buscar as unidades de uma cidade específica (com paginação)
export interface ProductUnitValuesParams extends Pagination.Params {
  productId: number
  state: string
  city: string
  isDependentContext?: boolean
}

// Arrays simples de strings — a API retorna apenas os nomes de estados e cidades
export type ProductStatesResponse = string[]
export type ProductCitiesResponse = string[]

// Formato bruto retornado pela API para cada unidade física do produto
export interface ProductUnitResponseItemRaw {
  name: string
  address: {
    location: string
    complement: string
    neighborhood: string
    city: string
    state: string
    country: string
    zipCode: string
  }
}

// Resposta bruta da API para a listagem de unidades (antes de formatar)
export interface ProductUnitsResponseRaw extends Pagination.Pageable<ProductUnitResponseItemRaw> {
  items: ProductUnitResponseItemRaw[]
}

// Unidade formatada para uso no wizard — endereço achatado em string única para exibição
export interface ProductUnitResponseItem {
  state: string
  city: string
  unit: string // nome da unidade — também usado como identificador único no wizard
  address: string // endereço completo formatado: "rua, complemento, bairro - cidade/UF"
}

// Resposta formatada da listagem de unidades
export interface ProductUnitsResponse extends Pagination.Pageable<ProductUnitResponseItem> {
  items: ProductUnitResponseItem[]
}

// Query keys do React Query — mantidas como constantes para garantir consistência
// entre componentes que compartilham as mesmas queries (ex: deduplicação entre
// EnrollmentUnitSelect e useEnrollmentWizardFlags para a query de estados)
export const PRODUCT_STATES_QUERY_KEY = 'product-states'
export const PRODUCT_CITIES_QUERY_KEY = 'product-cities'
export const PRODUCT_UNITS_QUERY_KEY = 'product-units'

// ─── Opções disponíveis (ofertas/turmas) ─────────────────────────────────────
//
// Tipos usados pela etapa de seleção de oferta do wizard.
// Cada oferta representa uma turma disponível com horário, turno, plano, etc.

// Formato bruto retornado pela API para cada oferta disponível
export interface ProductAvailableOptionOfferItemRaw {
  offerId: number
  startAt?: string | null | undefined // data de início da turma (ISO string)
  startTime?: string | null | undefined // horário de início (string bruta, ex: "19:00:00")
  shift?: string | null | undefined // turno (ex: "Noturno", "Manhã")
  frequency?: string | null | undefined // frequência de aulas (ex: "3x por semana")
  minuteClassDuration?: number | null | undefined // duração da aula em minutos
  coveredByPlan?: boolean | null // indica se a oferta está coberta pelo plano do usuário
  requiredPlanId?: number | null // ID do plano necessário para esta oferta
  workloadHours?: number | null // carga horária total
  monthlyDuration?: number | null // duração em meses
  additionalInfo?: string | null // informações adicionais livres
  enrollmentDeadline?: string | null // prazo final de matrícula (ISO string)
}

// Resposta paginada bruta da API para o endpoint /available-options
export type ProductAvailableOptionResponse = Pagination.Pageable<ProductAvailableOptionOfferItemRaw>

// Parâmetros da query de opções disponíveis — filtra por unidade e tipo de turma já selecionados
export interface ProductAvailableOptionsRequest {
  productId: number
  unitName?: string | null // nome da unidade física selecionada no wizard (null para online)
  classType?: string | null // tipo de turma selecionado (null quando não há seleção)
  isDependentContext?: boolean
  page?: number | null
  pageSize?: number | null
}

export const AVAILABLE_OPTIONS_QUERY_KEY = 'product-available-options'

// Tipo de turma disponível para o produto (ex: Individual, Em grupo)
export interface ProductAvailableOptionClassTypeItem {
  id: number
  displayName: string // nome exibido ao usuário
}

// Oferta formatada para uso no wizard — campos de data/hora e plano já processados
export interface ProductAvailableOptionOfferItem {
  offerId: number
  startAt?: string | null // data de início formatada (ex: "Jan 2025")
  startTime?: string | undefined // horário formatado com locale (ex: "19h")
  shift?: string | null // turno traduzido pelo modelo Shift
  frequency?: string | null
  minuteClassDuration?: string | undefined // duração formatada (ex: "1h30min")
  coveredByPlan?: boolean | null
  requiredPlanId?: number | null
  plan?: Plan.Enum // objeto do plano resolvido a partir de requiredPlanId
  workloadHours?: number | null
  monthlyDuration?: number | null
  additionalInfo?: string | null
  enrollmentDeadline?: string | null // prazo de matrícula formatado
}

// Resposta paginada formatada com ofertas já processadas
export type FormattedProductAvailableOptionResponse =
  Pagination.Pageable<ProductAvailableOptionOfferItem>

// ─── Extra Info (detalhes adicionais da oferta) ───────────────────────────────
//
// Dados complementares da oferta selecionada (ou do produto quando não há oferta).
// Buscados pelo endpoint /extra-info após a seleção de oferta estar concluída.
// O campo `id` corresponde ao offerId — usado para auto-selecionar quando a lista de ofertas está vazia.

export interface ProductExtraInfoResponse {
  id: number // offerId retornado pelo servidor (usado no double-check)
  workloadHours?: number | null
  monthlyDuration?: number | null
  additionalInfo?: string | null
  enrollmentDeadline?: string | null
  startAt?: string | null
  requiredPlanId?: number
}

// Parâmetros da query de extra info — offerId opcional: sem ele, o servidor retorna dados do produto
export interface ProductExtraInfoQuery {
  productId: number
  offerId?: number
}

export const EXTRA_INFO_QUERY_KEY = 'product-extra-info'

// ─── Tipos de turma ──────────────────────────────────────────────────────────

// Formato retornado pela API no endpoint /class-types
export interface ClassTypeResponse {
  id: number
  displayName: string
}

// Parâmetros da query de tipos de turma — filtra por unidade quando o produto tem unidades físicas
export interface ClassTypeRequest extends Pagination.Params {
  productId: number
  unitName?: string // nome da unidade selecionada; undefined para produtos online
  isDependentContext: boolean
}

// ─── Disponibilidade de plano ─────────────────────────────────────────────────
//
// Usada pelo EnrollmentPlanCoverage e pelo EnrollCta para determinar se o usuário
// precisa fazer upgrade de plano para acessar o conteúdo.

// Formato bruto da API — planos como IDs numéricos
export interface ProductAvailableInfoRawResponse {
  requiredPlans?: number[] // IDs dos planos que cobrem o produto
  coveredByPlan: boolean // true quando o plano atual do usuário cobre o produto
}

export type ProductAvailableInfoRequest = {
  productId: number
  isDependentContext: boolean
}

// Formato formatado — IDs resolvidos para objetos Plan.Enum com nome, cor, etc.
export type ProductAvailableInfoResponse = {
  requiredPlans: Plan.Enum[]
  coveredByPlan: boolean
}

export const PRODUCT_AVAILABLE_INFO_QUERY_KEY = 'product-available-info'
